import * as _ from 'lodash';
import { Attendance, getAttendanceOrdering, timeReportCommand, timeReportState } from '../../objects/Attendance';

/**
 * @typedef { import('../objects/Meeting').Meeting } Meeting
 * @typedef { import('../objects/Mission').Mission } Mission
*/

const attendanceOrdering = getAttendanceOrdering(x => x.attendance);

/**
 * Check if "a" and "b" is the same treating null and empty string as equal.
 * @param {string} a
 * @param {string} b
 */
function isChangedString(a, b) {
    if (a && b) {
        return a !== b;
    } else if (a || b) {
        return true;
    } else {
        return false;
    }
}

export class AttendanceModel {
    /**
     * @param {Attendance} attendance
     * @param {boolean} isPolitikerEnabled
     * @param {boolean} isNew
     */
    constructor(attendance, isPolitikerEnabled, isNew) {
        this.originalAttendance = null;
        this.attendance = attendance;
        this.comment = attendance.Comment;
        this.isPolitikerEnabled = isPolitikerEnabled;

        this.canRequestTimeReport = false;
        this.isRequestingTimeReport = false;
        this.isWaitingTimeReport = false;
        this.timeReportStatusText = null;

        if (isPolitikerEnabled && !attendance.isLegacy() && !attendance.CeremonyId) {
            if (this.hasTimeReport) {
                switch (this.attendance.TimeReport.State) {
                    case timeReportState.requested:
                        case timeReportState.reported:
                        this.timeReportStatusText = 'Väntar på svar';
                        this.isWaitingTimeReport = true;
                        break;
                    case timeReportState.rejected:
                        this.timeReportStatusText = 'Avslaget, väntar på nytt svar';
                        this.isWaitingTimeReport = true;
                }
            } else if (attendance.getPerson().hasPolitikerNow) {
                this.canRequestTimeReport = true;
                this.isRequestingTimeReport = attendance.Meeting.EnableTimereport && isNew;
                this.timeReportStatusText = 'Väntar på svar';
            }
        }

        this._presentTime = attendance.PresentTime;
        this._servingTime = attendance.ServingTime;

        this._isPresent = !!this.presentTime;
        this._isServing = !!this.servingTime;
    }

    get isAdded() {
        return !this.attendance.Id;
    }

    get hasTimeReport() {
        return !!this.attendance.TimeReport;
    }

    get isChanged() {
        if (this.isAdded) return false;
        if (this.originalAttendance != null) return true;
        if (this.canRequestTimeReport && this.isRequestingTimeReport) return true;
        if (!this.isWaitingTimeReport && !this.isRequestingTimeReport) {
            if (isChangedString(this.presentTime, this.attendance.PresentTime)) return true;
            if (isChangedString(this.servingTime, this.attendance.ServingTime)) return true;
        }
        if (isChangedString(this.comment, this.attendance.Comment)) return true;
        return false;
    }

    get isPresent() {
        return this._isPresent;
    }

    set isPresent(value) {
        value = !!value;
        if (this._isPresent !== value) {
            this._isPresent = value;
            if (value) {
                this.presentTime = this.attendance.Meeting.MeetingTime;
            } else {
                this.presentTime = "";
                this.isServing = false;
            }
        }
    }

    get presentTime() {
        return this._presentTime;
    }

    set presentTime(value) {
        this._presentTime = value;
        this._isPresent = !!value;
    }

    get isServing() {
        return this._isServing;
    }

    set isServing(value) {
        value = !!value;
        if (this._isServing !== value) {
            this._isServing = value;
            if (value) {
                this.servingTime = this.presentTime;
            } else {
                this.servingTime = "";
            }
        }
    }

    get servingTime() {
        return this._servingTime;
    }

    set servingTime(value) {
        this._servingTime = value;
        this._isServing = !!value;
    }

    get displayPerson() {
        return this.attendance.displayPerson;
    }

    get displayMissionType() {
        return this.attendance.displayMissionType;
    }

    canServeAsSubstitute() {
        return this.attendance.canServeAsSubstitute();
    }

    getPerson() {
        return this.attendance.getPerson();
    }

    getPersonId() {
        return this.attendance.getPersonId();
    }

    /**
     * 
     * @param {Meeting} meeting
     * @param {Mission} mission
     * @param {boolean} isPolitikerEnabled
     */
    static createNew(meeting, mission, isPolitikerEnabled) {
        const att = Attendance.createNew(meeting, mission);
        return new AttendanceModel(att, isPolitikerEnabled, true);
    }

    /**
     * 
     * @param {Attendance} attendance
     * @param {boolean} isPolitikerEnabled
     */
    static createExisting(attendance, isPolitikerEnabled) {
        return new AttendanceModel(attendance, isPolitikerEnabled, false);
    }

    /**
     * @param {Attendance} attendance
     */
    withAttendance(attendance) {
        let ret = AttendanceModel.createExisting(attendance, this.isPolitikerEnabled);
        ret.comment = this.comment;
        return ret;
    }

    /**
     * 
     * @param {AttendanceModel[]} collection
     */
    static order(collection) {
        return _.orderBy(collection, attendanceOrdering);
    }

    createUpdate() {
        let ret = {
            AttendanceId: this.attendance.Id,
            RowVersion: this.attendance.RowVersion,
            Attendance: {
                PresentTime: this.presentTime,
                ServingTime: this.servingTime,
                Comment: this.comment,
                SpecifyMissionId: this.originalAttendance != null ? this.attendance.MissionId : null,
            }
        };

        if (this.canRequestTimeReport && this.isRequestingTimeReport) {
            ret.Attendance.TimeReportOperation = timeReportCommand.add;
        }

        return ret;
    }

    createAdd() {
        let ret = {
            Attendance: {
                MissionId: this.attendance.MissionId,
                PresentTime: this.presentTime,
                ServingTime: this.servingTime,
                Comment: this.comment,
            }
        };

        if (this.canRequestTimeReport && this.isRequestingTimeReport) {
            ret.Attendance.RequestTimeReport = true;
        }

        return ret;
    }

    isLegacy() {
        return this.attendance.isLegacy();
    }

    canSpecifyMission() {
        return !!(this.attendance.isLegacy() || this.originalAttendance != null);
    }

    specifyMission(mission) {
        if (this.originalAttendance == null) {
            this.originalAttendance = this.attendance;
        }
        this.attendance = this.originalAttendance.specifyMission(mission);
    }
}